import React, { useState } from "react";
import { Header } from "../../../../../components";
import { Button, Input, Table, Modal, Form, Space, Popconfirm, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  UseFetchBlacklistTopics,
  UseCreateBlacklistTopic,
  UseUpdateBlacklistTopic,
  UseDeleteBlacklistTopic,
} from "../hooks/useFetch";

function BlacklistTopicPage() {
  const queryClient = useQueryClient();
  const { data: topics = [], isLoading } = UseFetchBlacklistTopics();
  const { mutateAsync: createTopic, isLoading: isCreating } = UseCreateBlacklistTopic();
  const { mutateAsync: updateTopic, isLoading: isUpdating } = UseUpdateBlacklistTopic();
  const { mutateAsync: deleteTopic } = UseDeleteBlacklistTopic();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const refresh = () => queryClient.invalidateQueries(["blacklistTopics"]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ topic: record.topic });
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await updateTopic({ id: editing.id, topic: values.topic });
        message.success("แก้ไขหัวข้อสำเร็จ");
      } else {
        await createTopic({ topic: values.topic });
        message.success("เพิ่มหัวข้อสำเร็จ");
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditing(null);
      refresh();
    } catch (error) {
      if (error?.errorFields) return; // validation
      await message.destroy();
      message.error(error?.response?.data?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTopic(id);
      message.success("ลบหัวข้อสำเร็จ");
      refresh();
    } catch (error) {
      await message.destroy();
      message.error("ลบไม่สำเร็จ");
    }
  };

  const columns = [
    { title: "ลำดับ", align: "center", width: 80, render: (_, __, i) => i + 1 },
    { title: "หัวข้อ Blacklist", dataIndex: "topic", key: "topic" },
    {
      title: "จัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            แก้ไข
          </Button>
          <Popconfirm
            title="ยืนยันการลบหัวข้อนี้?"
            onConfirm={() => handleDelete(record.id)}
            okText="ลบ"
            cancelText="ยกเลิก"
          >
            <Button size="small" danger>
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="md:m-10">
      <div className="flex justify-between items-center mb-6">
        <Header category="Admin" title="หัวข้อ Blacklist" />
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          เพิ่มหัวข้อ
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={topics}
        columns={columns}
        pagination={false}
        bordered
      />

      <Modal
        title={editing ? "แก้ไขหัวข้อ Blacklist" : "เพิ่มหัวข้อ Blacklist"}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => {
          setIsModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        okText="บันทึก"
        cancelText="ยกเลิก"
        okButtonProps={{ loading: isCreating || isUpdating }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="topic"
            label="หัวข้อ"
            rules={[{ required: true, message: "กรุณากรอกหัวข้อ" }]}
          >
            <Input placeholder="เช่น ฉ้อโกง, แอบอ้าง" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BlacklistTopicPage;
